/**
 * The Chat composer.
 *
 * One component in two sizes: large and centred under the welcome, compact
 * and docked under a conversation. Every control is either wired to something
 * real or visibly inert with the reason in its tooltip — a control that looks
 * live and does nothing costs more trust than one that admits what it is.
 *
 * Wired: the text, Send, the model choice (only where the plan permits more
 * than one), Search the web (only where it is offered) and Choose project on
 * a new conversation. Inert: attachments, approval, voice, plugins.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, type FormEvent, type ReactNode } from 'react';
import { FolderOpen, Globe, Loader2, Mic, Paperclip, Plug, Send, ShieldCheck, Sparkles } from 'lucide-react';
import type { AiTierView } from '@cenox/shared';
import { cn } from '../../../components/ui';

export type Mode = 'chat' | 'work';

/** A control that exists in the design and has nothing behind it yet. */
export function Inert({ icon, label, title }: { icon: ReactNode; label?: string; title: string }): ReactNode {
  return (
    <span
      title={title}
      aria-label={label ?? title}
      aria-disabled="true"
      className="inline-flex cursor-not-allowed select-none items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-400"
    >
      {icon}
      {label}
    </span>
  );
}

export interface ComposerHandle {
  focus: () => void;
}

interface ComposerProps {
  variant: 'hero' | 'dock';
  mode: Mode;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  /** CenoX AI is not open to this workspace: nothing can be typed or sent. */
  locked: boolean;
  /** An answer is arriving; the next question waits for it. */
  busy: boolean;
  /** What the plan permits. A choice is shown only where there is one. */
  models: AiTierView[];
  model: string;
  onModel: (tier: string) => void;
  /**
   * Why a model above the default is worth it. The server refuses an
   * escalated request without one — a stronger tier costs several times more
   * per token, and spending it should be a decision somebody states.
   */
  reason: string;
  onReason: (value: string) => void;
  /**
   * Choose project, when there is one to choose — on a new conversation in a
   * workspace with CenoX AI. Elsewhere the chip is shown inert.
   */
  projectControl?: ReactNode;
  lockedPlaceholder: string;
  /**
   * Search the web, when the server offers it to this workspace. Absent: the
   * control is not shown at all — it is a rollout, not an inert design item.
   */
  webSearch?: {
    on: boolean;
    onToggle: (next: boolean) => void;
    /** CenoX charges for a searched answer (false on the workspace's own key). */
    charged: boolean;
    remainingToday: number;
  };
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  { variant, mode, value, onChange, onSubmit, locked, busy, models, model, onModel, reason, onReason, projectControl, lockedPlaceholder, webSearch },
  ref,
) {
  const box = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => ({ focus: () => box.current?.focus() }), []);

  const hero = variant === 'hero';
  const escalated = !locked && model !== '' && models.length > 1;
  const escalatedLabel = models.find((t) => t.tier === model)?.label ?? 'this model';
  const canSend =
    !locked && !busy && mode === 'chat' && value.trim().length > 0 && (!escalated || reason.trim().length > 0);

  // Grow with the text, up to a ceiling, then scroll.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, hero ? 240 : 200)}px`;
  }, [value, hero]);

  const placeholder = locked
    ? lockedPlaceholder
    : mode === 'chat'
      ? hero
        ? 'Ask CenoX anything about this workspace…'
        : 'Reply to CenoX…'
      : 'Describe the work to carry out…';

  return (
    <form onSubmit={onSubmit} className="w-full">
      <div
        className={cn(
          'rounded-3xl border border-slate-200 bg-white transition-shadow',
          'focus-within:border-brand-300 focus-within:shadow-lg focus-within:shadow-brand-500/5',
          hero ? 'p-3 shadow-md shadow-slate-900/5 sm:p-4' : 'p-2.5 shadow-sm',
          locked && 'bg-slate-50',
        )}
      >
        <textarea
          ref={box}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter is a new line, and an IME mid-word is left alone.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (canSend) onSubmit(e as unknown as FormEvent);
            }
          }}
          rows={hero ? 3 : 1}
          disabled={locked || busy}
          placeholder={placeholder}
          aria-label="Your message"
          className={cn(
            'block w-full resize-none border-0 bg-transparent px-1.5 text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed',
            hero ? 'min-h-[4.5rem] py-1 text-base' : 'min-h-[2.25rem] py-1.5 text-sm',
          )}
        />

        {escalated && (
          <div className="mt-2 flex items-center gap-2 rounded-2xl bg-amber-50 px-3 py-2">
            <Sparkles className="size-3.5 shrink-0 text-amber-700" aria-hidden />
            <input
              value={reason}
              onChange={(e) => onReason(e.target.value)}
              maxLength={400}
              disabled={busy}
              aria-label={`Why use ${escalatedLabel}?`}
              placeholder={`Why use ${escalatedLabel}? It costs more per token — required`}
              className="min-w-0 flex-1 bg-transparent text-xs text-slate-900 outline-none placeholder:text-amber-700/70"
            />
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Inert icon={<Paperclip className="size-3.5" />} title="Attachments are not built yet" />
          <Inert
            icon={<ShieldCheck className="size-3.5" />}
            label="Ask for approval"
            title="Approval stops CenoX before a consequential action. Available when Work is built."
          />
          <Inert icon={<Mic className="size-3.5" />} title="Voice input is not built yet" />
          {!locked && webSearch && (
            <button
              type="button"
              aria-pressed={webSearch.on}
              disabled={busy || (!webSearch.on && webSearch.remainingToday <= 0)}
              onClick={() => webSearch.onToggle(!webSearch.on)}
              title={
                webSearch.remainingToday <= 0 && !webSearch.on
                  ? 'No web searches left today'
                  : `Look up current, public information and cite sources. Your business records are not sent with a web search.${
                      webSearch.charged ? ' Searches are charged to your AI credit.' : ''
                    } ${webSearch.remainingToday} left today.`
              }
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                webSearch.on
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              <Globe className="size-3.5" aria-hidden />
              Search the web
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Offered only where the plan permits more than one; named where it does not. */}
            {!locked && models.length > 1 && (
              <select
                value={model}
                onChange={(e) => onModel(e.target.value)}
                aria-label="Model"
                className="h-8 rounded-full border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none focus-visible:border-brand-400"
              >
                <option value="">{models[0]!.label}</option>
                {models.slice(1).map((t) => (
                  <option key={t.tier} value={t.tier}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
            {!locked && models.length === 1 && (
              <span
                title="Your plan includes one model"
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-slate-200 px-3 text-xs text-slate-600"
              >
                <Sparkles className="size-3.5 text-brand-600" aria-hidden />
                {models[0]!.label}
              </span>
            )}

            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send"
              title={mode === 'work' ? 'Work is not built yet' : locked ? 'Included with CenoX AI' : 'Send'}
              className={cn(
                'bg-cx-gradient flex shrink-0 items-center justify-center rounded-full text-white shadow-md shadow-indigo-500/25 transition-all',
                'hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                'disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none',
                hero ? 'size-10' : 'size-9',
              )}
            >
              {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Send className="size-4" aria-hidden />}
            </button>
          </div>
        </div>
      </div>

      <div className={cn('flex flex-wrap items-center gap-2 px-2', hero ? 'mt-3' : 'mt-2')}>
        {projectControl ??
          (hero ? (
            <Inert
              icon={<FolderOpen className="size-3.5" />}
              label="Choose project"
              title={locked ? 'Projects come with CenoX AI' : 'Move this conversation from its header'}
            />
          ) : null)}
        <Inert icon={<Plug className="size-3.5" />} label="Plugins" title="Plugins are not built yet" />
        <span className="ml-auto text-xs text-slate-400">
          {mode === 'work' ? 'Work is not built yet — Chat is.' : 'Included with CenoX AI'}
        </span>
      </div>
    </form>
  );
});
